import { Combobox } from "components/Combobox";
import Popover from "components/Popover";
import SjrQuartilesTooltip from "components/Popover/SjrQuartilesTooltip/SjrQuartilesTooltip";
import { QuartileBar } from "components/QuartileBar";
import useLabels from "hooks/useLabels";
import { useEffect, useMemo, useState } from "react";

const QUARTILES = ["1", "2", "3", "4"];

type Option = { value: string; label: string };
type SelectedItems = Option[];
type PropsType = {
  onSelectChange: (value: number | undefined) => void;
  sjrMax?: number;
};

const quartileToOption = (quartile: string): Option => ({
  value: quartile,
  label: `Q${quartile}`,
});

export const JournalRankSelect = ({ onSelectChange, sjrMax }: PropsType) => {
  const [filterLabels] = useLabels("filter");
  const [journalRank, setJournalRank] = useState<SelectedItems>([]);

  useEffect(() => {
    if (sjrMax) {
      const selectedValue = sjrMax;
      const items = Array.from({ length: selectedValue }, (value, index) =>
        quartileToOption(`${index + 1}`)
      );
      setJournalRank(items);
    } else {
      setJournalRank([]);
    }
  }, [sjrMax]);

  const renderSelectedJournalRank = (items: SelectedItems) => {
    if (items.length === 0) return filterLabels["journal-rank-placeholder"];
    const selectedItems = items
      .map((item) => Number(item.value))
      .sort((a, b) => b - a);
    const maxRank = selectedItems[0];
    return maxRank === 1 ? "Q" + maxRank : `Q1 - Q${maxRank}`;
  };

  const handleSelectChange = (selected: SelectedItems) => {
    const prevNums = journalRank.map((o) => Number(o.value));
    const currNums = selected.map((o) => Number(o.value));

    // no selection
    if (currNums.length === 0) {
      setJournalRank([]);
      onSelectChange?.(undefined);
      return;
    }

    // additions -------------------------------------------------
    if (currNums.length > prevNums.length) {
      const newMax = Math.max(...currNums);
      applyRange(newMax);
      return;
    }

    // removals (length decreased) ------------------------------
    const removed = prevNums.filter((n) => !currNums.includes(n))[0]; // only 1 change at a time
    const prevMax = Math.max(...prevNums);

    const newMax = removed === prevMax ? Math.max(...currNums) : removed; // rule 3

    applyRange(newMax);
  };

  const options = useMemo(() => {
    return QUARTILES.map((quartile) => ({
      value: quartile,
      label: `Q${quartile}`,
    }));
  }, []);

  const applyRange = (max: number) => {
    const range = Array.from({ length: max }, (_, i) =>
      quartileToOption(String(i + 1))
    );
    setJournalRank(range);
    onSelectChange?.(max);
  };

  return (
    <div>
      <div className="flex items-center gap-x-2 mb-2">
        <span className="sm-md">{filterLabels["journal-rank"]}</span>
        <Popover
          interactive
          maxWidth={334}
          tooltipContent={<SjrQuartilesTooltip />}
        >
          <i className="icon-info text-fg-muted" />
        </Popover>
      </div>
      <Combobox
        options={options}
        multiple={true}
        size="sm"
        getOptionLabel={(option) => {
          const quartile = (option as { value: string }).value;
          return (
            <div className="flex items-center gap-3 tiny-md outline-offset-0">
              Q{quartile} <QuartileBar quartile={Number(quartile)} />
            </div>
          );
        }}
        selectedOptions={journalRank}
        getSelectedDisplayValue={(selected) => {
          return renderSelectedJournalRank(selected as SelectedItems);
        }}
        onChange={(selected) => handleSelectChange(selected as SelectedItems)}
      />
    </div>
  );
};
